import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, CheckCircle2, XCircle, Clock, Stethoscope } from "lucide-react";
import { AICallout } from "@/components/makai/primitives";

export const Route = createFileRoute("/teacher/attendance")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Attendance — MAKAI" }] }),
  component: AttendancePage,
});

type Status = "present" | "absent" | "late" | "excused";
const STATUSES: { v: Status; label: string; icon: any; cls: string }[] = [
  { v: "present", label: "Present", icon: CheckCircle2, cls: "bg-primary-soft text-primary border-primary/30" },
  { v: "absent",  label: "Absent",  icon: XCircle,      cls: "bg-destructive/10 text-destructive border-destructive/30" },
  { v: "late",    label: "Late",    icon: Clock,        cls: "bg-gold/20 text-gold-foreground border-gold/30" },
  { v: "excused", label: "Excused", icon: Stethoscope,  cls: "bg-sky/15 text-sky border-sky/30" },
];

function AttendancePage() {
  const ctx = useTeacherContext();
  const [streamId, setStreamId] = useState<string>("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [learners, setLearners] = useState<{ id: string; first_name: string; last_name: string }[]>([]);
  const [marks, setMarks] = useState<Record<string, Status>>({});
  const [history, setHistory] = useState<{ date: string; present: number; total: number }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!streamId && ctx.streams.length) setStreamId(ctx.streams[0].id); }, [ctx.streams, streamId]);

  useEffect(() => {
    if (!streamId) return;
    (async () => {
      const [{ data: ls }, { data: att }] = await Promise.all([
        supabase.from("learners").select("id, first_name, last_name").eq("current_stream_id", streamId).eq("status","active").order("first_name"),
        supabase.from("attendance").select("learner_id, status").eq("stream_id", streamId).eq("date", date),
      ]);
      setLearners((ls ?? []) as any);
      const map: Record<string, Status> = {};
      for (const a of att ?? []) map[a.learner_id] = a.status as Status;
      // Default everyone to present if no record yet
      for (const l of ls ?? []) if (!map[l.id]) map[l.id] = "present";
      setMarks(map);

      // History (last 14 days)
      const since = new Date(Date.now() - 14 * 86400_000).toISOString().slice(0, 10);
      const { data: hist } = await supabase.from("attendance").select("date,status").eq("stream_id", streamId).gte("date", since);
      const grouped = new Map<string, { p: number; t: number }>();
      for (const r of hist ?? []) {
        const g = grouped.get(r.date) ?? { p: 0, t: 0 };
        g.t += 1;
        if (r.status === "present" || r.status === "late") g.p += 1;
        grouped.set(r.date, g);
      }
      setHistory(Array.from(grouped.entries()).sort((a, b) => b[0].localeCompare(a[0])).map(([d, g]) => ({ date: d, present: g.p, total: g.t })));
    })();
  }, [streamId, date]);

  const summary = useMemo(() => {
    const counts = { present: 0, absent: 0, late: 0, excused: 0 };
    for (const id of Object.keys(marks)) counts[marks[id]]++;
    return counts;
  }, [marks]);

  async function save() {
    if (!streamId || !ctx.staffId || !ctx.schoolId) return;
    setSaving(true);
    const rows = learners.map((l) => ({
      school_id: ctx.schoolId!, stream_id: streamId, learner_id: l.id, date,
      status: marks[l.id] ?? "present", recorded_by: ctx.staffId!,
    }));
    const { error } = await supabase.from("attendance").upsert(rows, { onConflict: "stream_id,learner_id,date" } as any);
    setSaving(false);
    if (error) toast.error(error.message); else toast.success(`Saved ${rows.length} records`);
  }

  function exportCsv() {
    const rows = [["Learner", "Status"], ...learners.map((l) => [`${l.first_name} ${l.last_name}`, marks[l.id] ?? ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `attendance-${date}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <AppShell role="teacher" title="Attendance" subtitle="Take and review daily attendance for your classes."
      action={<Button onClick={save} disabled={saving || !learners.length}>{saving ? "Saving…" : "Save attendance"}</Button>}>
      {ctx.streams.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No classes assigned yet.</div>
      ) : (
        <>
          <div className="grid gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Class</span>
              <select value={streamId} onChange={(e) => setStreamId(e.target.value)} className="rounded-lg border border-input bg-background px-3 py-2">
                {ctx.streams.map((s) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg border border-input bg-background px-3 py-2" />
            </label>
            <Button variant="outline" onClick={exportCsv} className="self-end"><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {STATUSES.map((s) => (
              <div key={s.v} className={`rounded-xl border p-3 ${s.cls}`}>
                <div className="flex items-center gap-2"><s.icon className="h-4 w-4" /><span className="text-xs font-semibold uppercase">{s.label}</span></div>
                <div className="mt-1 font-display text-2xl font-bold">{(summary as any)[s.v]}</div>
              </div>
            ))}
          </div>

          <section className="mt-4 rounded-2xl border border-border bg-card shadow-soft">
            <ul className="divide-y divide-border">
              {learners.map((l) => (
                <li key={l.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-gold text-xs font-bold text-gold-foreground">
                      {l.first_name[0]}{l.last_name[0]}
                    </div>
                    <span className="truncate font-medium">{l.first_name} {l.last_name}</span>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1">
                    {STATUSES.map((s) => {
                      const active = marks[l.id] === s.v;
                      return (
                        <button
                          key={s.v}
                          onClick={() => setMarks((m) => ({ ...m, [l.id]: s.v }))}
                          className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${active ? s.cls : "border-border text-muted-foreground hover:bg-muted"}`}
                        >{s.label}</button>
                      );
                    })}
                  </div>
                </li>
              ))}
              {!learners.length && <li className="px-4 py-8 text-center text-sm text-muted-foreground">No learners in this class.</li>}
            </ul>
          </section>

          <section className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
            <h3 className="mb-3 font-display text-lg font-bold">Recent history</h3>
            {history.length === 0 ? <div className="text-sm text-muted-foreground">No prior records.</div> : (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {history.map((h) => {
                  const pct = h.total ? Math.round((h.present / h.total) * 100) : 0;
                  return (
                    <button key={h.date} onClick={() => setDate(h.date)} className="rounded-xl border border-border bg-background/60 p-3 text-left hover:border-primary/40">
                      <div className="text-sm font-semibold">{new Date(h.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
                      <div className="mt-1 text-xs text-muted-foreground">{h.present}/{h.total} present · {pct}%</div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      <div className="mt-6">
        <AICallout
          description="Spot absentee trends and draft parent messages from this register."
          suggestions={[
            "Identify absentee trends this term",
            "Draft an SMS to parents of frequent absentees",
            "Predict learners at risk of dropping out",
          ]}
        />
      </div>
    </AppShell>
  );
}
